/** Flat geometry on the hall floor (x across, y towards the stage, metres). */

export type V2 = readonly [number, number];

export interface Closest {
  point: [number, number];
  distance: number;
  /** Position along the segment, 0 at its start, 1 at its end. */
  t: number;
  /** Index of the segment (polyline) it lies on. */
  segment: number;
}

export function sub(a: V2, b: V2): [number, number] {
  return [a[0] - b[0], a[1] - b[1]];
}

export function length(v: V2): number {
  return Math.hypot(v[0], v[1]);
}

export function distance(a: V2, b: V2): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

export function closestOnSegment(p: V2, a: V2, b: V2): Closest {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const point: [number, number] = [a[0] + t * dx, a[1] + t * dy];
  return { point, distance: distance(p, point), t, segment: 0 };
}

export function closestOnPolyline(p: V2, pts: readonly V2[], closed: boolean): Closest | null {
  let best: Closest | null = null;
  const n = pts.length;
  const segments = closed ? n : n - 1;
  for (let i = 0; i < segments; i++) {
    const c = closestOnSegment(p, pts[i]!, pts[(i + 1) % n]!);
    if (!best || c.distance < best.distance) best = { ...c, segment: i };
  }
  return best;
}

/** Even-odd rule; points exactly on an edge may land either side. */
export function pointInPolygon(p: V2, poly: readonly V2[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!;
    const [xj, yj] = poly[j]!;
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

const turn = (a: V2, b: V2, c: V2) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

/** Whether segments ab and cd cross each other properly; ends that only touch don't count. */
export function segmentsCross(a: V2, b: V2, c: V2, d: V2): boolean {
  const eps = 1e-12;
  return turn(a, b, c) * turn(a, b, d) < -eps && turn(c, d, a) * turn(c, d, b) < -eps;
}

/**
 * Whether two simple polygons overlap: one holds a corner of the other, or their edges cross. Shapes
 * that only touch may count either way (as with pointInPolygon), so shrink one a hair to allow contact.
 */
export function polygonsOverlap(a: readonly V2[], b: readonly V2[]): boolean {
  if (a.some((p) => pointInPolygon(p, b)) || b.some((p) => pointInPolygon(p, a))) return true;
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (segmentsCross(a[i]!, a[(i + 1) % a.length]!, b[j]!, b[(j + 1) % b.length]!)) return true;
    }
  }
  return false;
}

/** Positive when the points run anticlockwise seen from above. */
export function signedArea(poly: readonly V2[]): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    a += poly[j]![0] * poly[i]![1] - poly[i]![0] * poly[j]![1];
  }
  return a / 2;
}

export function bounds(poly: readonly V2[]): { min: [number, number]; max: [number, number] } {
  const min: [number, number] = [Infinity, Infinity];
  const max: [number, number] = [-Infinity, -Infinity];
  for (const [x, y] of poly) {
    min[0] = Math.min(min[0], x);
    min[1] = Math.min(min[1], y);
    max[0] = Math.max(max[0], x);
    max[1] = Math.max(max[1], y);
  }
  return { min, max };
}

/**
 * Keeps a circle (someone walking, radius r) out of a solid outline: returns where its centre
 * must move to, or null when it doesn't touch.
 */
export function pushOutOfSolid(c: V2, r: number, poly: readonly V2[]): [number, number] | null {
  const edge = closestOnPolyline(c, poly, true);
  if (!edge) return null;
  const inside = pointInPolygon(c, poly);
  if (!inside && edge.distance >= r) return null;
  const away = inside ? sub(edge.point, c) : sub(c, edge.point);
  const len = length(away);
  if (len < 1e-9) return null;
  const k = inside ? (edge.distance + r) / len : (r - edge.distance) / len;
  return [c[0] + away[0] * k, c[1] + away[1] * k];
}

/** Keeps a circle inside an outline (the room): returns where its centre must move to, or null. */
export function keepInside(c: V2, r: number, poly: readonly V2[]): [number, number] | null {
  const edge = closestOnPolyline(c, poly, true);
  if (!edge) return null;
  const inside = pointInPolygon(c, poly);
  if (inside && edge.distance >= r) return null;
  const toward = sub(edge.point, c);
  const len = length(toward);
  if (len < 1e-9) return null;
  const k = inside ? -(r - edge.distance) / len : (edge.distance + r) / len;
  return [c[0] + toward[0] * k, c[1] + toward[1] * k];
}

/** Points along a quadratic curve, ends included. */
export function sampleQuadratic(a: V2, control: V2, b: V2, steps: number): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    out.push([u * u * a[0] + 2 * u * t * control[0] + t * t * b[0], u * u * a[1] + 2 * u * t * control[1] + t * t * b[1]]);
  }
  return out;
}
